import { useEffect, useRef, useState } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'
import { api, can, type Fleet, type MapSettings, type Vehicle } from '../lib/api'
import { addBasemap, mapSettings } from '../lib/basemap'

/** The vehicles, on the dispatcher's map.
 *
 *  PLAN.html §16: "One map, layered. The dispatcher toggles what he wants to
 *  see — volunteers, vehicles, or both. There is no second map anywhere in the
 *  system." This is the vehicles layer; volunteer positions come from the
 *  panel and are not this component's job.
 *
 *  NOTHING HERE CAN STOP THE SCREEN
 *
 *  If the GPS platform is unreachable, switched off, or never configured, this
 *  renders a sentence saying which — and the queue, the open call and the
 *  address map carry on exactly as before. CLAUDE.md: "No dependency may block
 *  the screen from loading."
 *
 *  A VEHICLE WITH NO FIX IS LISTED, NOT PINNED
 *
 *  The same rule the geocoder follows: a coordinate is never guessed. A
 *  tracker that has never reported, or has lost its fix, appears in the list
 *  as "no position" — because a van that exists and cannot be seen is
 *  something the dispatcher needs to know, and silently omitting it makes it
 *  look like it was never in the fleet. */

/** The theme's own tokens, read at draw time.
 *
 *  theme.css: "Do not invent a colour here — if the dispatcher screen needs
 *  one, it goes in the mockup first." These were hard-coded to the DARK
 *  theme's hex values, so in light theme a vehicle's pin was one green and its
 *  row in the list below was a different one. */
//
//  EACH STATUS CARRIES ITS OWN FALLBACK, and that is the point.
//
//  This used to be one shared fallback of '#E0A33E' -- amber, "unknown" --
//  behind a lookup that could never succeed. Custom properties inherit
//  DOWNWARD, and every token is declared on `body[data-theme=...]`; the lookup
//  below read `document.documentElement`, which is <html>, the PARENT of body.
//  So `getPropertyValue` returned "" every single time and EVERY pin was
//  painted the unknown amber, in both themes, since the map shipped.
//
//  An online van and an offline van were the same colour on the map while the
//  list row underneath showed them correctly -- the same vehicle, two answers,
//  one screen. A shared fallback is what let a broken lookup look plausible;
//  per-status fallbacks mean the worst case is a slightly-off hue rather than
//  a wrong meaning.
const STATUS_TOKEN: Record<string, [token: string, fallback: string]> = {
  online: ['--go', '#2E8B62'],
  offline: ['--ink-faint', '#82949F'],
  unknown: ['--wait', '#A9720F'],
}

function token(name: string, fallback: string): string {
  if (typeof getComputedStyle !== 'function') return fallback
  // `document.body`, NOT documentElement. See above.
  const v = getComputedStyle(document.body).getPropertyValue(name)
  return v.trim() || fallback
}

function pin(v: Vehicle): L.DivIcon {
  const [name, fallback] = STATUS_TOKEN[v.status] ?? STATUS_TOKEN.unknown
  const colour = token(name, fallback)
  // An inline SVG, for the reason PlaceMap documents: Leaflet's default marker
  // is a PNG resolved relative to its own stylesheet, which a bundler rewrites
  // and then cannot find — the symptom is a working map with an invisible pin.
  const moving = v.speed_knots != null && v.speed_knots > 1
  return L.divIcon({
    className: 'vehicle-pin',
    html:
      '<svg viewBox="0 0 26 26" width="22" height="22" aria-hidden="true">' +
      `<circle cx="13" cy="13" r="9" fill="${colour}" stroke="#fff" stroke-width="2.5"/>` +
      (moving ? '<circle cx="13" cy="13" r="3.5" fill="#fff"/>' : '') +
      '</svg>',
    iconSize: [22, 22],
    iconAnchor: [11, 11],
  })
}

function speed(v: Vehicle): string {
  if (v.unlocated) return 'no position'
  if (v.speed_knots != null && v.speed_knots > 1) {
    // Traccar reports knots. Nobody dispatching a van thinks in knots.
    return `${Math.round(v.speed_knots * 1.852)} km/h`
  }
  return 'stopped'
}

export function FleetMap() {
  const box = useRef<HTMLDivElement | null>(null)
  const map = useRef<L.Map | null>(null)
  const markers = useRef<Map<number, L.Marker>>(new Map())
  const detach = useRef<(() => void) | null>(null)
  const [settings, setSettings] = useState<MapSettings | null>(null)
  const [note, setNote] = useState<string | null>(null)
  const [fleet, setFleet] = useState<Fleet | null>(null)

  useEffect(() => {
    let alive = true
    mapSettings().then((s) => { if (alive) setSettings(s) })
    return () => { alive = false }
  }, [])

  // The layer is a permission node (§19: Map > Vehicles layer). Not asking for
  // it at all is better than polling every fifteen seconds for a 403.
  const allowed = can('map.vehicles')

  useEffect(() => {
    if (!allowed) return
    let alive = true
    const load = () =>
      api.fleet()
        .then((f) => { if (alive) setFleet(f) })
        // Silent. A GPS platform that is down must not put an error banner on
        // the dispatcher's main screen.
        .catch(() => {})
    load()
    // Matched to the queue's 15s, not the ringing poll's 2s. A vehicle moves
    // continuously and a dispatcher glancing at where it is does not need it
    // to the second; a third fast timer on this screen would cost more than it
    // buys.
    const t = setInterval(load, 15_000)
    return () => { alive = false; clearInterval(t) }
  }, [allowed])

  const vehicles = fleet?.vehicles ?? []
  const located = vehicles.filter(
    (v) => !v.unlocated && v.latitude != null && v.longitude != null,
  )

  useEffect(() => {
    // THE MAP IS TORN DOWN WITH ITS DIV, NOT LEFT BOUND TO A DEAD ONE.
    //
    // The canvas only renders while something is locatable, so when every
    // vehicle loses its fix React unmounts that div -- while `map.current`
    // survives, still bound to a node no longer in the document. When a
    // vehicle regains a fix React mounts a NEW div, `!map.current` is false,
    // and nothing is ever drawn into it again: a permanently blank map that
    // only a page reload fixes.
    if (!settings?.enabled || !settings.tile_url || located.length === 0
        || !box.current) {
      if (map.current) {
        // Detached FIRST, or a Google load still in flight draws onto the
        // map just removed.
        detach.current?.()
        detach.current = null
        map.current.remove()
        map.current = null
        markers.current.clear()
      }
      return
    }

    if (!map.current) {
      map.current = L.map(box.current, {
        scrollWheelZoom: false,
        attributionControl: true,
      })
      // OpenStreetMap or Google, by the organization's map level. On Google
      // every rebuild above is another billed map load, which is the price of
      // not leaving a map bound to a dead div. lib/basemap.ts.
      detach.current = addBasemap(map.current, settings, setNote)
    }

    const seen = new Set<number>()
    for (const v of located) {
      seen.add(v.id)
      const at: L.LatLngExpression = [v.latitude as number, v.longitude as number]
      const existing = markers.current.get(v.id)
      const text = v.driver ? `${v.name} — ${v.driver} · ${speed(v)}`
                            : `${v.name} · ${speed(v)}`
      if (existing) {
        existing.setLatLng(at)
        existing.setIcon(pin(v))
        existing.setTooltipContent(text)
      } else {
        markers.current.set(
          v.id,
          L.marker(at, { icon: pin(v) })
            .bindTooltip(text, { direction: 'top' })
            .addTo(map.current),
        )
      }
    }
    // A vehicle that lost its fix loses its pin rather than keeping the last
    // one. A stale pin is a claim about where something is NOW.
    for (const [id, marker] of markers.current) {
      if (!seen.has(id)) {
        marker.remove()
        markers.current.delete(id)
      }
    }

    map.current.fitBounds(
      L.latLngBounds(
        located.map((v) => [v.latitude as number, v.longitude as number] as [number, number]),
      ),
      { padding: [24, 24], maxZoom: 15 },
    )
    const t = setTimeout(() => map.current?.invalidateSize(), 60)
    return () => clearTimeout(t)
  }, [settings, fleet])

  useEffect(() => () => {
    detach.current?.()
    detach.current = null
    map.current?.remove()
    map.current = null
    markers.current.clear()
  }, [])

  if (!allowed) return null

  if (fleet && !fleet.available) {
    return (
      <div className="pending">
        <div className="mark">No vehicles</div>
        {fleet.detail || 'The GPS platform is not configured.'}
      </div>
    )
  }
  if (fleet && vehicles.length === 0) {
    return (
      <div className="pending">
        <div className="mark">No vehicles</div>
        This account can see no vehicles on the GPS platform yet.
      </div>
    )
  }
  if (!fleet) {
    return (
      <div className="pending">
        <div className="mark">Loading</div>
        Asking the GPS platform where the vehicles are.
      </div>
    )
  }

  return (
    <>
      {settings?.enabled && located.length > 0 && (
        <div ref={box} className="fleet-canvas" />
      )}
      {/* Why this is not the Google map the level promises. Only while a map
          is drawn: a note about the map under no map is noise. */}
      {note && settings?.enabled && located.length > 0 && (
        <p className="fleet-note">{note}</p>
      )}
      {/* The devices answered and the positions did not. Without this, that
          renders as every van reading "no position" -- identical to a fleet
          whose trackers have all genuinely gone dark, which is a different
          problem with a different fix. */}
      {fleet?.detail && (
        <p className="fleet-note">{fleet.detail}</p>
      )}
      <div className="fleetlist">
        {vehicles.map((v) => (
          <div className={`frow ${v.unlocated ? 'lost' : ''}`} key={v.id}>
            <span className={`s ${v.status}`} />
            <span className="fname">{v.name}</span>
            {/* Who is driving, when a fob has been read. Blank is honest: it
                means nobody has identified themselves, not that nobody is in
                the vehicle. */}
            {v.driver && <span className="fdriver">{v.driver}</span>}
            <span className="fst">{speed(v)}</span>
          </div>
        ))}
      </div>
    </>
  )
}
